import {
    Box,
    Button,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { UserRound } from 'lucide-react';
import { type Ref } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabelText } from '~/components/Fields/LabeledValue';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import FilterGroupChip from './FilterGroupChip';
import {
    filterTags,
    hasActivePatientFilters,
    patientFilterGroups,
    type ConfiguredFilterValues,
    type PatientFilterFacetId,
    type PatientFilterGroup,
    type PatientFilters,
} from './model';
import { ConfigurationLoadFailure } from './shared';

export type PatientListFilterBarProps = {
    filters: PatientFilters;
    /** The configured sites and tags the bar offers. */
    configured: ConfiguredFilterValues;
    /** The "assigned to me" toggle; absent where it is not offered. */
    assignedToMe?: AssignedToMeToggleProps;
    onToggle: (facetId: PatientFilterFacetId, value: string) => void;
    onClearGroup: (group: PatientFilterGroup) => void;
    onClear: () => void;
    /** The first filter control, which takes focus when the filters are cleared. */
    firstControlRef: Ref<HTMLButtonElement>;
    /** Told when a filter menu opens and closes. */
    onMenuOpenChange: (open: boolean) => void;
};

const ROW_SX = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 0.75,
    minWidth: 0,
} as const;

/**
 * The list's filters: the clinical tags offered as filters, then a chip per
 * filter control, the "assigned to me" toggle and a clear-all control. Each
 * dimension has one control showing its own selection.
 */
export default function PatientListFilterBar({
    filters,
    configured,
    assignedToMe,
    onToggle,
    onClearGroup,
    onClear,
    firstControlRef,
    onMenuOpenChange,
}: PatientListFilterBarProps) {
    const intl = useIntl();
    const groups = patientFilterGroups(
        configured,
        filters,
        intl.formatMessage({ id: 'patientList.care.unknownSite' })
    );
    const tags = filterTags(
        configured,
        filters,
        intl.formatMessage({ id: 'patientList.tags.unknown' })
    );
    const tagColor = new Map(
        (configured.tags ?? []).map((tag) => [tag.id, tag.color] as const)
    );
    const tagsLabel = intl.formatMessage({
        id: 'patientList.filter.facet.tag',
    });

    return (
        <Box
            role="group"
            aria-label={intl.formatMessage({ id: 'patientList.filter.region' })}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
        >
            <ConfigurationLoadFailure />
            {tags.length > 0 && (
                <Box sx={ROW_SX}>
                    <LabelText aria-hidden sx={{ mr: 0.5 }}>
                        {tagsLabel}
                    </LabelText>
                    <ToggleButtonGroup
                        size="small"
                        value={[...filters.tag]}
                        aria-label={tagsLabel}
                    >
                        {tags.map((tag) => (
                            <ToggleButton
                                key={tag.value}
                                value={tag.value}
                                onChange={() => onToggle('tag', tag.value)}
                                sx={{ gap: 0.75 }}
                            >
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor:
                                            tagColor.get(tag.value) ??
                                            'text.secondary',
                                    }}
                                />
                                {tag.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            )}
            <Box sx={ROW_SX}>
                {groups.map((group, index) => (
                    <FilterGroupChip
                        key={group.id}
                        ref={index === 0 ? firstControlRef : undefined}
                        group={group}
                        filters={filters}
                        onToggle={onToggle}
                        onClear={onClearGroup}
                        onMenuOpenChange={onMenuOpenChange}
                    />
                ))}
                {assignedToMe && (
                    <>
                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ mx: 0.5 }}
                        />
                        <AssignedToMeToggle {...assignedToMe} />
                    </>
                )}
                {hasActivePatientFilters(filters) && (
                    <Button size="small" onClick={onClear}>
                        <FormattedMessage id="patientList.filter.clear" />
                    </Button>
                )}
            </Box>
        </Box>
    );
}

/** The one value of the "assigned to me" toggle group. */
const ASSIGNED_TO_ME = 'assignedToMe';

type AssignedToMeToggleProps = {
    /** Whether the list is narrowed to the signed-in user's patients. */
    pressed: boolean;
    /**
     * Narrows to (or widens from) the signed-in user's patients; undefined
     * while the user is not known as a user of the installation, when the
     * control is offered but disabled.
     */
    onChange: ((pressed: boolean) => void) | undefined;
};

/**
 * The "assigned to me" toggle. Disabled, with the reason reachable from the
 * keyboard, while the user is not known as a user of the installation: there
 * is then no "me" to match.
 */
function AssignedToMeToggle({ pressed, onChange }: AssignedToMeToggleProps) {
    const intl = useIntl();
    return (
        <DisabledHint
            title={
                onChange === undefined
                    ? intl.formatMessage({
                          id: 'patientList.assignedToMe.unavailable',
                      })
                    : undefined
            }
        >
            {/* A group of one, so the toggle is boxed like the tag toggles. */}
            <ToggleButtonGroup
                size="small"
                value={pressed ? [ASSIGNED_TO_ME] : []}
                disabled={onChange === undefined}
                onChange={() => onChange?.(!pressed)}
            >
                <ToggleButton value={ASSIGNED_TO_ME} sx={{ gap: 0.75 }}>
                    <UserRound size={14} aria-hidden />
                    <FormattedMessage id="patientList.assignedToMe" />
                </ToggleButton>
            </ToggleButtonGroup>
        </DisabledHint>
    );
}
